import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { filter, map, take } from 'rxjs';

import { TranslateService } from '../../../core/i18n';
import { EstablishmentStore } from '../store/establishment.store';

/**
 * Helper to show access denied message and redirect to dashboard
 */
function denyAccess(
  router: Router,
  messageService: MessageService,
  translateService: TranslateService,
) {
  messageService.add({
    severity: 'warn',
    summary: translateService.get('common.warning'),
    detail: translateService.get('auth.noPermission'),
    life: 5000,
  });
  return router.createUrlTree(['/dashboard']);
}

/**
 * Guard that restricts access to admin and owner roles only.
 * Users with 'user' role will be redirected to dashboard with a toast notification.
 */
export const adminRoleGuard: CanActivateFn = () => {
  const establishmentStore = inject(EstablishmentStore);
  const router = inject(Router);
  const messageService = inject(MessageService);
  const translateService = inject(TranslateService);

  // If store is not loading and we have a current establishment, check immediately
  const isLoading = establishmentStore.isLoading();
  const currentEstablishment = establishmentStore.currentEstablishment();

  if (!isLoading && currentEstablishment) {
    const canAccess = establishmentStore.canAccessTransactions();
    if (!canAccess) {
      return denyAccess(router, messageService, translateService);
    }
    return true;
  }

  // If loading, wait for it to complete then check
  return toObservable(establishmentStore.isLoading).pipe(
    filter((loading) => !loading),
    take(1),
    map(() => {
      const canAccess = establishmentStore.canAccessTransactions();
      if (!canAccess) {
        return denyAccess(router, messageService, translateService);
      }
      return true;
    }),
  );
};

/**
 * Guard that restricts access to owner role only.
 */
export const ownerRoleGuard: CanActivateFn = () => {
  const establishmentStore = inject(EstablishmentStore);
  const router = inject(Router);
  const messageService = inject(MessageService);
  const translateService = inject(TranslateService);

  // If store is not loading and we have a current establishment, check immediately
  const isLoading = establishmentStore.isLoading();
  const currentEstablishment = establishmentStore.currentEstablishment();

  if (!isLoading && currentEstablishment) {
    const canAccess = establishmentStore.isOwner();
    if (!canAccess) {
      return denyAccess(router, messageService, translateService);
    }
    return true;
  }

  // If loading, wait for it to complete then check
  return toObservable(establishmentStore.isLoading).pipe(
    filter((loading) => !loading),
    take(1),
    map(() => {
      const canAccess = establishmentStore.isOwner();
      if (!canAccess) {
        return denyAccess(router, messageService, translateService);
      }
      return true;
    }),
  );
};
